/**
 * The card's class string, split out of `Panel.tsx` so non-component modules can
 * import it without pulling a React component in (and so `Panel.tsx` keeps a
 * component-only export surface for react-refresh — the same split
 * `cta-classes.ts` makes for the CTA).
 *
 * It exists because the card kept being re-typed. Before this, the identical
 * hairline + raise-gradient + lift recipe was hand-written in four places in the
 * docs shell alone (the body's card grid, the prev/next pager, and both card
 * grids on the docs index), each with its own slightly different shadow and
 * focus ring. A card that differs by a few pixels between two surfaces of one
 * page is exactly the drift the redesign consolidated.
 */

export interface PanelStyleProps {
  /** Paint for a dark band ground rather than the light page. */
  readonly onDark?: boolean | undefined;
  /** Lift on hover, and carry a focus ring. Only for panels that are a link. */
  readonly interactive?: boolean | undefined;
  readonly className?: string | undefined;
}

/**
 * The light variant carries a barely-there vertical gradient (`#fbfbfc → #fff`)
 * rather than a flat fill — on a white page a flat-white card is invisible
 * without a heavy border, and the gradient lets the border stay a hairline.
 *
 * The focus ring rides on `interactive` rather than being a separate flag: an
 * interactive panel is by definition a link or a button, and a keyboard user
 * must be able to see which one they are on. A non-interactive `<div>` panel
 * never takes focus, so the classes are inert there — which is what makes it
 * safe to bundle them and impossible to forget.
 */
export function panelClasses({
  onDark = false,
  interactive = false,
  className,
}: PanelStyleProps = {}): string {
  return [
    'relative overflow-hidden rounded-[var(--bd-radius)]',
    onDark
      ? 'border border-[color:var(--bd-band-line)] bg-[color:var(--bd-band-fill)]'
      : 'border border-[color:var(--bd-line)] bg-gradient-to-b from-[color:var(--bd-raise)] to-[color:var(--bd-page)]',
    interactive
      ? 'transition-[transform,box-shadow,border-color] duration-300 ' +
        'hover:-translate-y-0.5 motion-reduce:hover:translate-y-0 ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
        (onDark
          ? // Cyan rather than blue on a dark ground, and the offset ring is
            // painted with the band's own colour so the halo reads as a gap
            // rather than as a white outline on black.
            'hover:border-[color:var(--bd-band-line-2)] focus-visible:ring-[color:var(--bd-cyan)] focus-visible:ring-offset-[color:var(--bd-band)]'
          : 'hover:border-[color:var(--bd-line-3)] hover:shadow-[0_24px_48px_-32px_rgba(12,13,16,.45)] focus-visible:ring-[color:var(--bd-blue)]')
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}
